import {
	getVariableInfo,
	getVariableToken,
} from '@dealstack/frontend/shared/components/EmailTemplates/utils/emailVariableCatalog';
import { groupVariables } from '@dealstack/frontend/shared/components/EmailTemplates/utils/variableGroups';
import { useVariableGroupLabel } from '@dealstack/frontend/shared/components/EmailTemplates/hooks/useVariableGroupLabel';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Text, Tooltip } from '@mantine/core';
import { IconCheck, IconCopy, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import styled from 'styled-components';

import { GhostIconButton } from './ghostIconButton.styled';

interface Props {
	variables: string[];
	onInsert: (token: string) => void;
}

const Wrapper = styled.div`
	padding: 1rem;
	border-top: 1px solid var(--mantine-color-gray-4);
`;

const GroupLabel = styled.p`
	margin: 0.75rem 0 0.375rem;
	color: var(--mantine-color-gray-9);
	font-size: 0.75rem;
	line-height: 1.125rem;
	letter-spacing: 0.01em;
	text-transform: uppercase;

	&:first-of-type {
		margin-top: 0;
	}
`;

const Row = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.5rem;
	margin: 0 -0.5rem;
	padding: 0.375rem 0.5rem;
	border-radius: var(--mantine-radius-sm);
	text-align: left;
	cursor: pointer;

	&:hover,
	&:focus-visible {
		background-color: var(--mantine-color-gray-0);
	}
`;

const RowLabel = styled.span`
	min-width: 0;
	overflow: hidden;
	color: var(--mantine-color-text);
	font-size: 0.8125rem;
	line-height: 1.21875rem;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const Actions = styled.span`
	display: flex;
	align-items: center;
	gap: 0.125rem;
	flex-shrink: 0;
`;

const IconButton = styled(GhostIconButton)<{ $copied?: boolean }>`
	${({ $copied }) => ($copied ? 'color: var(--mantine-color-teal-8);' : '')}

	&:hover:not(:disabled) {
		${({ $copied }) => ($copied ? 'color: var(--mantine-color-teal-8);' : '')}
	}
`;

export const DataReferencePanel = ({ variables, onInsert }: Props) => {
	const tMipManage = useTranslationMipManage();
	const groupLabel = useVariableGroupLabel();
	const [copied, setCopied] = useState<string | null>(null);
	const groups = groupVariables(variables);

	const copy = (token: string) => {
		void navigator.clipboard.writeText(token);
		setCopied(token);
		window.setTimeout(() => setCopied((current) => (current === token ? null : current)), 1500);
	};

	if (groups.length === 0) {
		return (
			<Wrapper>
				<Text size="sm" c="gray.9">
					{tMipManage('email_templates.variables.none')}
				</Text>
			</Wrapper>
		);
	}

	return (
		<Wrapper>
			<Text fw={600} fz="0.8125rem" lh="1.21875rem" mb={4}>
				{tMipManage('email_templates.editor.data_reference.title', {
					defaultValue: 'Data reference',
				})}
			</Text>
			<Text fz="0.75rem" lh="1.125rem" c="gray.9" mb="0.75rem">
				{tMipManage('email_templates.editor.data_reference.hint', {
					defaultValue: 'Insert into the subject or body, or copy to paste anywhere.',
				})}
			</Text>

			{groups.map((entry) => (
				<div key={entry.group}>
					<GroupLabel>{groupLabel(entry.group)}</GroupLabel>
					{entry.variables.map((name) => {
						const info = getVariableInfo(name);
						const token = getVariableToken(name);
						const isCopied = copied === token;
						const canInsert = entry.group !== 'links';

						return (
							<Row
								key={name}
								role="button"
								tabIndex={0}
								aria-label={tMipManage('email_templates.variables.copy_aria', {
									label: info.label,
								})}
								// Keeps the editor's caret while the row is clicked.
								onMouseDown={(event) => event.preventDefault()}
								onClick={() => copy(token)}
								onKeyDown={(event) => {
									if (
										event.target === event.currentTarget &&
										(event.key === 'Enter' || event.key === ' ')
									) {
										event.preventDefault();
										copy(token);
									}
								}}
							>
								<RowLabel>{info.label}</RowLabel>
								<Actions>
									<Tooltip label={token} withArrow>
										<IconButton
											as="span"
											$size={24}
											$copied={isCopied}
											aria-hidden
										>
											{isCopied ? (
												<IconCheck size={14} />
											) : (
												<IconCopy size={14} />
											)}
										</IconButton>
									</Tooltip>
									{canInsert && (
										<Tooltip
											label={tMipManage('email_templates.editor.insert', {
												defaultValue: 'Insert where you were last typing',
											})}
											withArrow
										>
											<IconButton
												type="button"
												$size={24}
												onMouseDown={(event) => event.preventDefault()}
												aria-label={tMipManage(
													'email_templates.variables.insert_aria',
													{
														action: tMipManage(
															'email_templates.editor.insert',
															{
																defaultValue:
																	'Insert where you were last typing',
															}
														),
														label: info.label,
													}
												)}
												onClick={(event) => {
													event.stopPropagation();
													onInsert(token);
												}}
											>
												<IconPlus size={14} />
											</IconButton>
										</Tooltip>
									)}
								</Actions>
							</Row>
						);
					})}
				</div>
			))}
		</Wrapper>
	);
};
